<template>
  <div
    class="address-fields"
    style="display: flex; flex-direction: column; row-gap: 1rem"
    :aria-busy="isLookingUp ? 'true' : 'false'"
  >
    <div class="form-grid-1">
      <div class="form-field">
        <label
          class="form-field-label"
          :class="{ 'form-field-label-required': !optional }"
          :for="fieldId('country')"
        >
          País
        </label>
        <select
          :id="fieldId('country')"
          class="field"
          data-address-field="country"
          :value="fieldValue('country')"
          :aria-invalid="Boolean(errors.country)"
          :aria-describedby="errorId('country')"
          @change="handleCountryChange"
        >
          <option value="">Selecione</option>
          <option
            v-for="countryOption in countries"
            :key="countryOption.Codigo"
            :value="countryOption.Codigo"
          >
            {{ countryOption.Descricao }}
          </option>
        </select>
        <p
          v-if="errors.country"
          :id="errorId('country')"
          class="form-field-error"
        >
          {{ errors.country }}
        </p>
      </div>
    </div>

    <template v-if="showAddressFields">
      <div class="form-grid-2">
        <div class="form-field">
          <div class="flex items-center justify-between">
            <label
              class="form-field-label form-field-label-required"
              :for="fieldId('postalCode')"
            >
              {{ postalCodeLabel }}
            </label>
            <a
              v-if="isBrazil"
              target="_blank"
              rel="noopener noreferrer"
              class="form-link text-xs"
              href="https://buscacepinter.correios.com.br/app/endereco/index.php"
            >
              Não sei o CEP
            </a>
          </div>
          <input
            :id="fieldId('postalCode')"
            class="field"
            type="text"
            autocomplete="postal-code"
            data-address-field="postalCode"
            :value="postalCodeValue"
            :inputmode="isBrazil ? 'numeric' : 'text'"
            :maxlength="isBrazil ? 9 : null"
            :aria-invalid="Boolean(errors.postalCode)"
            :aria-describedby="errorId('postalCode')"
            @input="handlePostalInput"
            @change="handlePostalChange"
          />
          <p
            v-if="errors.postalCode"
            :id="errorId('postalCode')"
            class="form-field-error"
          >
            {{ errors.postalCode }}
          </p>
        </div>

        <AddressTextField
          field="number"
          label="Número"
          :id-prefix="idPrefix"
          :value="fieldValue('number')"
          :error="errors.number"
          @input="updateField('number', $event)"
          @change="commitField('number', $event)"
        />
      </div>

      <div class="form-grid-1">
        <AddressTextField
          field="street"
          label="Endereço/Logradouro"
          :id-prefix="idPrefix"
          :value="fieldValue('street')"
          :error="errors.street"
          :disabled="streetFieldsLocked"
          @input="updateField('street', $event)"
          @change="commitField('street', $event)"
        />
      </div>

      <div class="form-grid-2">
        <AddressTextField
          field="complement"
          label="Complemento"
          :id-prefix="idPrefix"
          :value="fieldValue('complement')"
          :error="errors.complement"
          :required="false"
          @input="updateField('complement', $event)"
          @change="commitField('complement', $event)"
        />
        <AddressTextField
          field="neighborhood"
          label="Bairro"
          :id-prefix="idPrefix"
          :value="fieldValue('neighborhood')"
          :error="errors.neighborhood"
          :disabled="streetFieldsLocked"
          @input="updateField('neighborhood', $event)"
          @change="commitField('neighborhood', $event)"
        />
      </div>

      <div class="form-grid-2">
        <AddressTextField
          field="city"
          label="Cidade"
          :id-prefix="idPrefix"
          :value="fieldValue('city')"
          :error="errors.city"
          :disabled="locationFieldsLocked"
          @input="updateField('city', $event)"
          @change="commitField('city', $event)"
        />

        <div class="form-field">
          <label
            class="form-field-label form-field-label-required"
            :for="fieldId('region')"
          >
            {{ isBrazil ? "UF" : "Estado / Província / Região" }}
          </label>
          <select
            v-if="isBrazil"
            :id="fieldId('region')"
            class="field"
            data-address-field="region"
            :disabled="locationFieldsLocked"
            :value="fieldValue('region')"
            :aria-invalid="Boolean(errors.region)"
            :aria-describedby="errorId('region')"
            @change="commitField('region', $event.target.value)"
          >
            <option value="">Selecione</option>
            <option v-for="uf in ufs" :key="uf" :value="uf">
              {{ uf }}
            </option>
          </select>
          <input
            v-else
            :id="fieldId('region')"
            class="field"
            type="text"
            autocomplete="address-level1"
            data-address-field="region"
            :value="fieldValue('region')"
            :aria-invalid="Boolean(errors.region)"
            :aria-describedby="errorId('region')"
            @input="updateField('region', $event.target.value)"
            @change="commitField('region', $event.target.value)"
          />
          <p
            v-if="errors.region"
            :id="errorId('region')"
            class="form-field-error"
          >
            {{ errors.region }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import AddressTextField from "@/components/address/AddressTextField.vue";
import { validarCampoCep, ufs } from "@/utils/formUtils";
import {
  ADDRESS_FIELD_MAP,
  cepBrasilValido,
  normalizarCodigoPostal,
  paisEhBrasil,
  rotuloCodigoPostal,
  validarEndereco,
} from "@/utils/enderecoUtils";

export default {
  name: "EnderecoFields",
  components: { AddressTextField },
  props: {
    modelValue: { type: Object, required: true },
    countries: { type: Array, default: () => [] },
    fieldMap: { type: Object, default: () => ({}) },
    idPrefix: { type: String, default: "address" },
    optional: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      errors: {},
      isLookingUp: false,
      locationFieldsLocked: false,
      streetFieldsLocked: false,
      lookupSequence: 0,
    };
  },
  computed: {
    resolvedFields() {
      return { ...ADDRESS_FIELD_MAP, ...this.fieldMap };
    },
    country() {
      return this.fieldValue("country");
    },
    showAddressFields() {
      return Boolean(this.country);
    },
    isBrazil() {
      return paisEhBrasil(this.country);
    },
    postalCodeLabel() {
      return rotuloCodigoPostal(this.country);
    },
    postalCodeValue() {
      const value = this.fieldValue("postalCode");
      return this.isBrazil ? normalizarCodigoPostal(value, this.country) : value;
    },
    ufs() {
      return ufs;
    },
  },
  mounted() {
    const postalCode = this.fieldValue("postalCode");
    if (this.isBrazil && postalCode) {
      this.lookupBrazilianPostal(postalCode, true);
    }
  },
  methods: {
    fieldId(field) {
      return `${this.idPrefix}-${field}`;
    },
    errorId(field) {
      return `${this.fieldId(field)}-error`;
    },
    fieldValue(field) {
      return this.modelValue?.[this.resolvedFields[field]] ?? "";
    },
    updateField(field, value) {
      this.patchFields({ [field]: value });
      if (this.errors[field]) {
        const errors = { ...this.errors };
        delete errors[field];
        this.errors = errors;
      }
    },
    commitField(field, value) {
      this.patchFields({ [field]: value }, true);
    },
    patchFields(values, notifyChange = false) {
      const updated = { ...this.modelValue };
      Object.entries(values).forEach(([field, value]) => {
        updated[this.resolvedFields[field]] = value;
      });
      this.$emit("update:modelValue", updated);
      if (notifyChange) {
        this.$emit("change", updated);
      }
    },
    handleCountryChange(event) {
      this.lookupSequence += 1;
      this.isLookingUp = false;
      this.locationFieldsLocked = false;
      this.streetFieldsLocked = false;
      this.errors = {};
      this.patchFields({
        country: event.target.value,
        postalCode: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        region: "",
      }, true);
    },
    handlePostalInput(event) {
      const normalized = normalizarCodigoPostal(event.target.value, this.country);
      event.target.value = normalized;
      this.updateField("postalCode", normalized);
    },
    async handlePostalChange(event) {
      const postalCode = normalizarCodigoPostal(event.target.value, this.country);
      event.target.value = postalCode;
      if (postalCode !== this.fieldValue("postalCode")) {
        this.updateField("postalCode", postalCode);
        await this.$nextTick();
      }
      if (!this.isBrazil) {
        this.$emit("change", this.modelValue);
        return;
      }

      if (!postalCode) {
        this.locationFieldsLocked = false;
        this.streetFieldsLocked = false;
        this.clearLocatedFields();
        return;
      }

      if (!cepBrasilValido(postalCode)) {
        const validation = validarEndereco(this.modelValue, this.resolvedFields, {
          optional: this.optional,
        });
        this.errors = {
          ...this.errors,
          postalCode: validation.errors.postalCode,
        };
        this.$emit("change", this.modelValue);
        return;
      }

      await this.lookupBrazilianPostal(postalCode, false);
    },
    async lookupBrazilianPostal(postalCode, onlyValidate) {
      const lookupId = ++this.lookupSequence;
      this.isLookingUp = true;
      const lookupAddress = { Cep: postalCode };

      try {
        const found = await validarCampoCep(lookupAddress, {
          somenteValidar: onlyValidate,
        });
        const currentPostalCode = normalizarCodigoPostal(
          this.fieldValue("postalCode"),
          this.country,
        );
        if (
          lookupId !== this.lookupSequence ||
          !this.isBrazil ||
          currentPostalCode !== normalizarCodigoPostal(postalCode, this.country)
        ) {
          return;
        }

        this.locationFieldsLocked = found;
        this.streetFieldsLocked =
          found &&
          Boolean(
            onlyValidate
              ? this.fieldValue("street")
              : lookupAddress.Logradouro,
          );

        if (onlyValidate) return;

        if (found) {
          this.patchFields({
            street: lookupAddress.Logradouro || "",
            neighborhood: lookupAddress.Bairro || "",
            city: lookupAddress.Cidade || "",
            region: lookupAddress.Uf || "",
          }, true);
        } else {
          this.clearLocatedFields();
        }
      } finally {
        if (lookupId === this.lookupSequence) {
          this.isLookingUp = false;
        }
      }
    },
    clearLocatedFields() {
      this.patchFields({
        street: "",
        neighborhood: "",
        city: "",
        region: "",
      }, true);
    },
    validate(showErrors = true) {
      const result = validarEndereco(this.modelValue, this.resolvedFields, {
        optional: this.optional,
      });
      if (showErrors) {
        this.errors = result.errors;
      }
      return result;
    },
    resetValidation() {
      this.errors = {};
    },
    refreshLookup() {
      this.lookupSequence += 1;
      this.locationFieldsLocked = false;
      this.streetFieldsLocked = false;
      const postalCode = this.fieldValue("postalCode");
      if (this.isBrazil && postalCode) {
        return this.lookupBrazilianPostal(postalCode, true);
      }
      return Promise.resolve(false);
    },
  },
};
</script>
